/**
 * useUseCaseApplicationMap
 *
 * Client-side join of approved use cases (Galileo) with user-owned applications (Fusion).
 * For each use case, finds the application whose aiUseCaseIds[] contains it — at most one,
 * enforcing the 1:1 use-case-to-application constraint.
 *
 * Replace the client-side reverse lookup with a federated AiUseCase.linkedApplication field
 * once the Neo4j HAS_USE_CASE reverse resolver is available in Fusion.
 */
import { useContext, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { UserContext } from '../../contexts/UserContext';
import { UserOwnedAppsQuery } from '../apps/UserOwnedAppsQuery';
import { useApprovedUseCases } from './useApprovedUseCases';
import { AiUseCase } from './types';
import { MyApp } from '../apps/types/myAppTypes';

export interface UseCaseWithApplication {
  useCase: AiUseCase;
  linkedApplication: MyApp | null;
}

interface UserOwnedAppsData {
  identity: {
    user: {
      resources: {
        applicationsPaged: {
          applications: MyApp[];
        };
      };
    };
  };
}

export interface UseUseCaseApplicationMapResult {
  rows: UseCaseWithApplication[];
  applications: MyApp[];
  loading: boolean;
  refetching: boolean;
  error: Error | undefined;
  reload: () => void;
}

/** Joins the current user's AI use cases with the applications they are linked to. */
export function useUseCaseApplicationMap(): UseUseCaseApplicationMapResult {
  const { login } = useContext(UserContext);

  const {
    useCases,
    loading: useCasesLoading,
    error: useCasesError,
    reload: reloadUseCases,
  } = useApprovedUseCases();

  const {
    loading: appsLoading,
    error: appsError,
    data: appsData,
    refetch: refetchApps,
  } = useQuery<UserOwnedAppsData>(UserOwnedAppsQuery, {
    variables: {
      login,
      filter: {
        havingRoles: ['owner', 'assistant_owner', 'approver'],
        includeSystemTypes: ['App', 'AiSystem'],
      },
      pagination: { limit: 100, offset: 0 },
    },
    skip: !login,
    errorPolicy: 'all',
  });

  const applications: MyApp[] = useMemo(
    () =>
      appsData?.identity?.user?.resources?.applicationsPaged?.applications ??
      [],
    [appsData],
  );

  // Build a map: useCaseId → MyApp for O(1) lookup.
  const useCaseToAppMap = useMemo(() => {
    const map = new Map<string, MyApp>();
    for (const app of applications) {
      for (const ucId of app.aiUseCaseIds ?? []) {
        map.set(ucId, app);
      }
    }
    return map;
  }, [applications]);

  const rows: UseCaseWithApplication[] = useMemo(
    () =>
      useCases.map(useCase => ({
        useCase,
        linkedApplication: useCaseToAppMap.get(useCase.useCaseId) ?? null,
      })),
    [useCases, useCaseToAppMap],
  );

  const [refetching, setRefetching] = useState(false);

  const reload = async () => {
    setRefetching(true);
    try {
      reloadUseCases();
      await refetchApps();
    } finally {
      setRefetching(false);
    }
  };

  return {
    rows,
    applications,
    loading: useCasesLoading || appsLoading,
    refetching,
    error:
      useCasesError ?? (appsError ? new Error(appsError.message) : undefined),
    reload,
  };
}
